import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { fetchNotificationPreferences, updateNotificationPreferences } from '../../redux/settingsSlice';
import ToastService from '../../core/notifications/ToastService';

export const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { notificationPreferences, isLoading, error } = useSelector((state: RootState) => state.settings);
  
  const [formData, setFormData] = useState({
    email_notifications: true,
    sms_notifications: true,
    push_notifications: true,
    notification_types: {
      new_booking: true,
      booking_cancelled: true,
      payout_processed: true,
      system_updates: true,
      low_balance: true,
      new_review: true
    },
    quiet_hours: {
      enabled: false,
      start_time: '22:00',
      end_time: '08:00'
    }
  });

  useEffect(() => {
    dispatch(fetchNotificationPreferences());
  }, [dispatch]);

  useEffect(() => {
    if (notificationPreferences) {
      setFormData(notificationPreferences);
    }
  }, [notificationPreferences]);

  const handleToggle = (field: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field as keyof typeof prev]
    }));
  };

  const handleNotificationTypeToggle = (type: string) => {
    setFormData(prev => ({
      ...prev,
      notification_types: {
        ...prev.notification_types,
        [type]: !prev.notification_types[type as keyof typeof prev.notification_types]
      }
    }));
  };

  const handleQuietHoursToggle = () => {
    setFormData(prev => ({
      ...prev,
      quiet_hours: {
        ...prev.quiet_hours,
        enabled: !prev.quiet_hours.enabled
      }
    }));
  };

  const handleTimeChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      quiet_hours: {
        ...prev.quiet_hours,
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await dispatch(updateNotificationPreferences(formData)).unwrap();
      await dispatch(fetchNotificationPreferences());
      ToastService.success('Notification preferences updated successfully');
    } catch (err: any) {
      ToastService.error(err || 'Failed to update preferences');
    }
  };

  if (isLoading && !notificationPreferences) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton
          onClick={() => navigate('/settings')}
          sx={{ display: { xs: 'flex', sm: 'none' } }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/settings')}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Notification Preferences
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', display: { xs: 'none', sm: 'block' } }}>
            Configure how you receive notifications
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* Notification Channels */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              Notification Channels
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.email_notifications}
                      onChange={() => handleToggle('email_notifications')}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Email Notifications</Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Receive notifications via email
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.sms_notifications}
                      onChange={() => handleToggle('sms_notifications')}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">SMS Notifications</Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Receive notifications via SMS
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.push_notifications}
                      onChange={() => handleToggle('push_notifications')}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Push Notifications</Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Receive push notifications on your device
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Notification Types */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              Notification Types
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.notification_types.new_booking}
                      onChange={() => handleNotificationTypeToggle('new_booking')}
                    />
                  }
                  label="New Booking"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.notification_types.booking_cancelled}
                      onChange={() => handleNotificationTypeToggle('booking_cancelled')}
                    />
                  }
                  label="Booking Cancelled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.notification_types.payout_processed}
                      onChange={() => handleNotificationTypeToggle('payout_processed')}
                    />
                  }
                  label="Payout Processed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.notification_types.system_updates}
                      onChange={() => handleNotificationTypeToggle('system_updates')}
                    />
                  }
                  label="System Updates"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.notification_types.low_balance}
                      onChange={() => handleNotificationTypeToggle('low_balance')}
                    />
                  }
                  label="Low Balance Alert"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.notification_types.new_review}
                      onChange={() => handleNotificationTypeToggle('new_review')}
                    />
                  }
                  label="New Review"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Quiet Hours */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              Quiet Hours
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.quiet_hours.enabled}
                  onChange={handleQuietHoursToggle}
                />
              }
              label="Enable Quiet Hours"
              sx={{ mb: 2 }}
            />
            {formData.quiet_hours.enabled && (
              <>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="time"
                      label="Start Time"
                      value={formData.quiet_hours.start_time}
                      onChange={(e) => handleTimeChange('start_time', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="time"
                      label="End Time"
                      value={formData.quiet_hours.end_time}
                      onChange={(e) => handleTimeChange('end_time', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      You won't receive notifications during quiet hours
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            onClick={() => navigate('/settings')}
            disabled={isLoading}
            fullWidth={window.innerWidth < 600}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{ backgroundColor: '#14B8A6' }}
            fullWidth={window.innerWidth < 600}
          >
            {isLoading ? 'Saving...' : 'Save Preferences'}
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default NotificationsPage;
